// アイテムマスタ照会と、そこから entrydata / 明細へ反映する処理をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createItemMasterService(deps) {
    const {
        DicVal,
        PDT_SIZE_PATTERN_FIELD_IDS,
        applyFirstExistingValue,
        assignEntryDataCurrent,
        currentPdtSizePatternCode,
        dispatch,
        entrydata,
        entrydataRef,
        entryrerender,
        fnc,
        formstatus,
        isEmpty,
        isSyohin2SectionEditable,
        loadPdtSizePatternOptions,
        loadPdtSizePatternSizeOptions,
        loadPdtTenkaiSizePatternInfoAfterEntryLoad,
        protectHbSeasonCommit,
        refreshPdtItmcItemCdDependentOptions,
        rerender,
        resolveBrandGroupBrandCode,
        resolveCurrentOppiCode,
        resolveItemMasterBrandCode,
        resolveItemMasterItemCode,
        setPdtItemCodesToDetailGrid,
        setPdtItmcItemCdOnEntryData,
        syncItemDependentData
    } = deps;

    function applyItemMasterInfoToEntryData(itemMasterInfo) {
        if (isEmpty(entrydataRef.current) || isEmpty(itemMasterInfo)) return false;
        let updated = false;
        // 主アイテム(BITM_CODE)は「空欄、かつ商品情報2セクションが編集可」の場合のみ反映する。
        // 入力済みの主アイテムや編集不可セクションをサブアイテム確定で上書きしない(連動仕様)。
        const mainItemTargets = ["BITM_CODE", "BITM_Code", "txtItemBITMCd", "PDT_BITMCD"];
        const currentMainItem = mainItemTargets
            .map((key) => String(DicVal(entrydataRef.current, key, "") ?? "").trim())
            .find((text) => text !== "") || "";
        const nextMainItem = String(DicVal(itemMasterInfo, "BITM_Code", "") ?? "").trim();
        if (currentMainItem === "" && nextMainItem !== "" && isSyohin2SectionEditable()) {
            if (!applyFirstExistingValue(entrydataRef.current, mainItemTargets, nextMainItem)) {
                entrydataRef.current.BITM_CODE = nextMainItem;
            }
            entrydataRef.current.BITM_CODE_nm = String(DicVal(itemMasterInfo, "BIT_Name", "") ?? "").trim();
            updated = true;
        }
        const mappings = [
            { targets: ["CITM_CODE", "CITM_Code", "txtAItmCd", "PDT_AITMCD"], value: DicVal(itemMasterInfo, "CITM_Code", "") },
            { targets: ["BITM_CITMSUBCODE", "BITM_CITMSubCode", "txtAItmcCd", "PDT_AITMCCD"], value: DicVal(itemMasterInfo, "BITM_CITMSubCode", "") },
            { targets: ["CIT_SBNCODE", "CITM_SBNCode", "txtSBNCode", "PDT_SBNCODE"], value: DicVal(itemMasterInfo, "CITM_SBNCode", "") },
            { targets: ["PDT_QRC_PACKIRISU", "QRCB_QRC_PackIrisu", "txtQRC_PackIrisu"], value: DicVal(itemMasterInfo, "QRCB_QRC_PackIrisu", "") },
            { targets: ["PDT_QRC_MINHACHUTANI", "QRCB_QRC_MinHachuTani", "txtQRC_MinHachuTani"], value: DicVal(itemMasterInfo, "QRCB_QRC_MinHachuTani", "") },
            { targets: ["PDT_QRC_SYOHINBUNRUIiCD", "PDT_QRC_SYOHINBUNRUICD", "QRCB_QRC_SyohinBunruiCd", "txtQRC_SyohinBunruiCd"], value: DicVal(itemMasterInfo, "QRCB_QRC_SyohinBunruiCd", "") },
            { targets: ["QRCB_QRC_SyohinComent", "txtQRC_SyohinComent", "PDT_QRC_SYOHINCOMENT"], value: DicVal(itemMasterInfo, "QRCB_QRC_SyohinComent", "") }
        ];
        mappings.forEach((mapping) => {
            if (applyFirstExistingValue(entrydataRef.current, mapping.targets, mapping.value)) {
                updated = true;
            }
        });

        const itemMasterSizePatternCode = String(DicVal(itemMasterInfo, "BITM_SIZEPATTERNCODE", "")).trim();
        if (isEmpty(itemMasterSizePatternCode)) {
            return updated;
        }

        const sizePatternChanged = applyFirstExistingValue(entrydataRef.current, PDT_SIZE_PATTERN_FIELD_IDS, itemMasterSizePatternCode);
        return updated || sizePatternChanged;
    }
    function loadItemMasterInfo(options) {
        const requestOptions = isEmpty(options) ? {} : options;
        if (isEmpty(entrydataRef.current)) return;
        const brandCode = resolveItemMasterBrandCode(entrydataRef.current);
        const itemCode = resolveItemMasterItemCode(entrydataRef.current);
        if (isEmpty(brandCode) || isEmpty(itemCode)) return;

        fnc.current.postaxios("hinbanmaster/GetItemMasterInfo",
            { BrandCode: brandCode, ItemCode: itemCode },
            {
                onSuccess: function (data) {
                    if (String(DicVal(data.returnData, "Found", "false")).toLowerCase() !== "true") return;
                    const changed = applyItemMasterInfoToEntryData(data.returnData);
                    loadPdtSizePatternOptions({ silent: true, autoSelectFirst: true });
                    loadPdtSizePatternSizeOptions(currentPdtSizePatternCode(entrydataRef.current), { silent: true });
                    if (!changed) return;
                    dispatch(entrydata({ ...entrydataRef.current }));
                    dispatch(entryrerender());
                },
                onError: function (data) {
                    if (!DicVal(requestOptions, "silent", true)) {
                        let msg = "品番マスタ情報の取得に失敗しました";
                        if (!isEmpty(data.errDescription)) msg = data.errDescription;
                        fnc.current.warnmsg(msg);
                    }
                }
            }, true
        );
    }
    function loadBitmSubCode(options) {
        if (formstatus === "ENTRY FORM") return;
        const requestOptions = isEmpty(options) ? {} : options;
        const targetData = isEmpty(DicVal(requestOptions, "targetData", null))
            ? entrydataRef.current
            : DicVal(requestOptions, "targetData", null);
        if (isEmpty(targetData)) return;

        const brandCode = String(resolveBrandGroupBrandCode(targetData)).trim();
        if (isEmpty(brandCode)) {
            const changed = setPdtItmcItemCdOnEntryData(targetData, "");
            if (changed) {
                assignEntryDataCurrent(protectHbSeasonCommit(targetData, "targetData"), "targetData");
                dispatch(entrydata({ ...entrydataRef.current }));
                dispatch(entryrerender());
            }
            syncItemDependentData({ silent: DicVal(requestOptions, "silent", true) });
            return;
        }

        fnc.current.postaxios("hinbanmaster/GetBitmSubCode",
            {
                PDT_BRDCODE: brandCode,
                PDT_SHOHINKBN: String(DicVal(targetData, "PDT_SHOHINKBN", "")).trim()
            },
            {
                onSuccess: function (data) {
                    const nextValue = String(DicVal(data.returnData, "BITM_SUBCODE", "")).trim();
                    const changed = setPdtItmcItemCdOnEntryData(targetData, nextValue);
                    if (changed) {
                        assignEntryDataCurrent(protectHbSeasonCommit(targetData, "targetData"), "targetData");
                        dispatch(entrydata({ ...entrydataRef.current }));
                        dispatch(entryrerender());
                    }
                    syncItemDependentData({ silent: DicVal(requestOptions, "silent", true) });
                    refreshPdtItmcItemCdDependentOptions(targetData, { silent: DicVal(requestOptions, "silent", true) });
                },
                onError: function (data) {
                    if (!DicVal(requestOptions, "silent", true)) {
                        let msg = "品目サブコードの取得に失敗しました";
                        if (!isEmpty(data.errDescription)) msg = data.errDescription;
                        fnc.current.warnmsg(msg);
                    }
                }
            }, true
        );
    }
    function loadPdtItemCodes(options) {
        const requestOptions = isEmpty(options) ? {} : options;
        const requestCode = resolveCurrentOppiCode();
        if (isEmpty(requestCode)) return;

        fnc.current.postaxios("hinbanmaster/GetPdtItemCodes",
            { OppiCode: requestCode },
            {
                onSuccess: function (data) {
                    if (String(DicVal(data.returnData, "Found", "false")).toLowerCase() !== "true") {
                        setPdtItemCodesToDetailGrid([]);
                        loadPdtTenkaiSizePatternInfoAfterEntryLoad();
                        return;
                    }
                    const itemCodes = [];
                    for (let i = 1; i <= 10; i++) {
                        itemCodes.push(DicVal(data.returnData, `PDT_ITMCITEMCD${i}`, ""));
                    }
                    setPdtItemCodesToDetailGrid(itemCodes);
                    // rerender()(全パネル再構築)は不要。グリッドへは setPdtItemCodesToDetailGrid が
                    // ag-grid API 経由で反映済みで、全再構築は表示のクリアにしか見えない。
                    loadPdtTenkaiSizePatternInfoAfterEntryLoad();
                },
                onError: function (data) {
                    // 失敗時もサイズ展開の後続ロードは実行する。ここで打ち切ると
                    // 展開コードありの品番でサイズ(210)グリッドが空行のまま残る。
                    loadPdtTenkaiSizePatternInfoAfterEntryLoad();
                    if (!DicVal(requestOptions, "silent", true)) {
                        let msg = "品目コード情報の取得に失敗しました";
                        if (!isEmpty(data.errDescription)) msg = data.errDescription;
                        fnc.current.warnmsg(msg);
                    }
                }
            }, true
        );
    }

    return {
        applyItemMasterInfoToEntryData,
        loadItemMasterInfo,
        loadBitmSubCode,
        loadPdtItemCodes
    };
}
